import axiosInstance from "./axiosInstance";
import logger from "../utils/logger";
import { extractErrorMessage } from "../utils/errorHandler";

export interface ReferralLink {
    id: string;
    title: string;
    description?: string;
    linkName: string;
    code: string;
    expiresAt?: string | null;
    recruiterId?: number;
    recruiterName?: string;
    isActive: boolean;
    visitCount: number;
    uniqueVisitorCount: number;
    linkedWorkersCount: number;
    lastVisitedAt?: string;
    metaAdId?: string | null;
    metaCampaignId?: string | null;
    createdBy: number;
    creatorName?: string;
    createdAt: string;
    updatedAt: string;
    fullUrl?: string;
    waMeUrl?: string;
    canEdit?: boolean;
}

export interface CreateReferralLinkRequest {
    title: string;
    description?: string;
    linkName?: string;
    recruiterId?: number;
    isActive?: boolean;
    // Pass an ISO string to override the 90-day default; pass null to opt out
    // of expiry entirely; omit the field to accept the default.
    expiresAt?: string | null;
    metaAdId?: string | null;
    metaCampaignId?: string | null;
}

export interface UpdateReferralLinkRequest {
    id: string;
    title?: string;
    description?: string;
    recruiterId?: number;
    isActive?: boolean;
    expiresAt?: string | null;
    metaAdId?: string | null;
    metaCampaignId?: string | null;
}

export interface PublicReferralLink {
    title: string;
    description?: string;
    recruiterName?: string;
    isActive: boolean;
    code: string;
    waMeUrl: string;
}

export interface ReferralLinkStats {
    referralLinkId: string;
    visits: number;
    uniqueVisitors: number;
    conversationsStarted: number;
    sessionsByConfidence: Record<string, number>;
    linkedWorkers: number;
    /** sessionsStarted → linkedWorkers conversion as a 0..1 ratio. */
    workerConversionRate: number;
    firstVisitAt?: string | null;
    lastVisitAt?: string | null;
}

export const fetchReferralLinksRequest = async (): Promise<ReferralLink[]> => {
    try {
        logger.info("Fetching referral links");
        const response = await axiosInstance.get<ReferralLink[]>('/api/referral-links');
        logger.info("Referral links fetched successfully");
        logger.debug("Referral links payload", response.data);
        return response.data;
    } catch (error: unknown) {
        const message = extractErrorMessage(error);
        logger.error("Error fetching referral links:", message);
        throw error;
    }
};

export const fetchReferralLinkByIdRequest = async (id: string): Promise<ReferralLink> => {
    try {
        logger.info(`Fetching referral link with ID: ${id}`);
        const response = await axiosInstance.get<ReferralLink>(`/api/referral-links/${id}`);
        logger.info("Referral link fetched successfully");
        logger.debug("Referral link payload", response.data);
        return response.data;
    } catch (error: unknown) {
        const message = extractErrorMessage(error);
        logger.error(`Error fetching referral link ${id}:`, message);
        throw error;
    }
};

export const createReferralLinkRequest = async (data: CreateReferralLinkRequest): Promise<ReferralLink> => {
    try {
        logger.info("Creating new referral link");
        logger.debug("Create referral link payload", data);
        const response = await axiosInstance.post<ReferralLink>('/api/referral-links', data);
        logger.info("Referral link created successfully");
        logger.debug("Created referral link", response.data);
        return response.data;
    } catch (error: unknown) {
        const message = extractErrorMessage(error);
        logger.error("Error creating referral link:", message);
        throw error;
    }
};

export const updateReferralLinkRequest = async ({ id, ...data }: UpdateReferralLinkRequest): Promise<ReferralLink> => {
    try {
        logger.info(`Updating referral link ${id}`);
        logger.debug("Update payload", data);
        const response = await axiosInstance.put<ReferralLink>(`/api/referral-links/${id}`, data);
        logger.info("Referral link updated successfully");
        logger.debug("Updated referral link", response.data);
        return response.data;
    } catch (error: unknown) {
        const message = extractErrorMessage(error);
        logger.error(`Error updating referral link ${id}:`, message);
        throw error;
    }
};

export const deleteReferralLinkRequest = async (id: string): Promise<void> => {
    try {
        logger.info(`Deleting referral link ${id}`);
        await axiosInstance.delete(`/api/referral-links/${id}`);
        logger.info("Referral link deleted successfully");
    } catch (error: unknown) {
        const message = extractErrorMessage(error);
        logger.error(`Error deleting referral link ${id}:`, message);
        throw error;
    }
};

export const toggleReferralLinkActiveRequest = async (id: string): Promise<ReferralLink> => {
    try {
        logger.info(`Toggling active status for referral link ${id}`);
        const response = await axiosInstance.put<ReferralLink>(`/api/referral-links/${id}/toggle-active`);
        logger.info("Referral link active status toggled successfully");
        logger.debug("Toggled referral link", response.data);
        return response.data;
    } catch (error: unknown) {
        const message = extractErrorMessage(error);
        logger.error(`Error toggling referral link ${id}:`, message);
        throw error;
    }
};

export const fetchPublicReferralLinkRequest = async (linkName: string): Promise<PublicReferralLink> => {
    try {
        logger.info(`Fetching public referral link: ${linkName}`);
        const response = await axiosInstance.get<PublicReferralLink>(`/api/referral-links/by-name/${linkName}`);
        logger.info("Public referral link fetched successfully");
        logger.debug("Public referral link payload", response.data);
        return response.data;
    } catch (error: unknown) {
        const message = extractErrorMessage(error);
        logger.error(`Error fetching public referral link ${linkName}:`, message);
        throw error;
    }
};

export const fetchReferralLinkStatsRequest = async (id: string): Promise<ReferralLinkStats> => {
    try {
        logger.info(`Fetching referral link stats: ${id}`);
        const response = await axiosInstance.get<ReferralLinkStats>(`/api/referral-links/${id}/stats`);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error fetching referral link stats ${id}:`, extractErrorMessage(error));
        throw error;
    }
};

export const recordReferralLinkVisitRequest = async (linkName: string): Promise<void> => {
    try {
        logger.info(`Recording visit for referral link: ${linkName}`);
        await axiosInstance.post(`/api/referral-links/visit/${linkName}`);
        logger.info("Referral link visit recorded successfully");
    } catch (error: unknown) {
        const message = extractErrorMessage(error);
        logger.error(`Error recording visit for referral link ${linkName}:`, message);
        throw error;
    }
};